import React, { useState, useCallback, useEffect } from 'react';
import RichDescriptionEditor, { preserveImageDimensions } from './RichDescriptionEditor';
import './SectionalDescriptionEditor.css';

const BLOCK_STEP = 'step';
const BLOCK_PARAGRAPH = 'paragraph';
const BLOCK_STEP_GROUP_START = 'stepGroupStart';

const DEFAULT_SECTIONS = [
  { type: BLOCK_PARAGRAPH, title: '', content: '' },
  { type: BLOCK_STEP, title: '', content: '' },
];

function parseSections(value) {
  if (!value || typeof value !== 'string') return [...DEFAULT_SECTIONS];
  const trimmed = value.trim();
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((s) => ({
          type: s.type === BLOCK_PARAGRAPH ? BLOCK_PARAGRAPH : s.type === BLOCK_STEP_GROUP_START ? BLOCK_STEP_GROUP_START : BLOCK_STEP,
          title: typeof s.title === 'string' ? s.title : '',
          content: typeof s.content === 'string' ? s.content : '',
        }));
      }
    } catch {
      /* fall through to legacy */
    }
  }
  return [{ type: BLOCK_STEP, title: '', content: trimmed }];
}

function serializeSections(sections) {
  return JSON.stringify(sections.map((s) => ({
    type: s.type || BLOCK_STEP,
    title: s.title || '',
    content: preserveImageDimensions(s.content || ''),
  })));
}

function getStepNumber(sections, index) {
  let stepNum = 0;
  for (let i = 0; i <= index; i++) {
    if (sections[i].type === BLOCK_STEP_GROUP_START) stepNum = 0;
    else if (sections[i].type === BLOCK_STEP) stepNum += 1;
  }
  return stepNum;
}

export default function SectionalDescriptionEditor({ value, onChange, placeholder }) {
  const [sections, setSections] = useState(() => parseSections(value));

  useEffect(() => {
    setSections(parseSections(value));
  }, [value]);

  const emitChange = useCallback(
    (newSections) => {
      setSections(newSections);
      onChange(serializeSections(newSections));
    },
    [onChange]
  );

  const updateSection = (index, field, val) => {
    const next = [...sections];
    next[index] = { ...next[index], [field]: val };
    emitChange(next);
  };

  const addStep = () => {
    emitChange([...sections, { type: BLOCK_STEP, title: '', content: '' }]);
  };

  const addParagraph = () => {
    emitChange([...sections, { type: BLOCK_PARAGRAPH, title: '', content: '' }]);
  };

  const addStepGroupStart = () => {
    emitChange([...sections, { type: BLOCK_STEP_GROUP_START, title: '', content: '' }]);
  };

  const insertAfter = (afterIndex, type) => {
    const next = [...sections];
    const newBlock = { type, title: '', content: '' };
    next.splice(afterIndex + 1, 0, newBlock);
    emitChange(next);
  };

  const removeSection = (index) => {
    if (sections.length <= 1) return;
    const next = sections.filter((_, i) => i !== index);
    emitChange(next);
  };

  const moveSection = (index, direction) => {
    const next = [...sections];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    emitChange(next);
  };

  const duplicateSection = (index) => {
    const copy = { ...sections[index] };
    const next = [...sections];
    next.splice(index + 1, 0, copy);
    emitChange(next);
  };

  return (
    <div className="sectional-description-editor step-instructions-editor">
      <div className="sectional-editor-header">
        <span className="sectional-editor-label">Step-by-step instructions</span>
        <div className="sectional-editor-header-actions">
          <button type="button" className="admin-btn admin-btn-primary admin-btn-small" onClick={addStep}>
            + Add step
          </button>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={addParagraph}>
            + Add paragraph
          </button>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={addStepGroupStart} title="Insert a marker to start a new step sequence (Step 1, 2, 3...)">
            Reset step counter
          </button>
        </div>
      </div>
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          {section.type === BLOCK_STEP_GROUP_START ? (
            <div className="sectional-editor-block step-group-start-block">
              <div className="sectional-editor-block-header" style={{ alignItems: 'center', gap: '0.5rem' }}>
                <span className="step-group-start-label">⟳ New step group (next step will be Step 1)</span>
                <div className="sectional-editor-block-actions">
                  <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => moveSection(index, -1)} disabled={index === 0} title="Move up">↑</button>
                  <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => moveSection(index, 1)} disabled={index === sections.length - 1} title="Move down">↓</button>
                  <button type="button" className="admin-btn admin-btn-danger admin-btn-small" onClick={() => removeSection(index)} title="Remove">Remove</button>
                </div>
              </div>
            </div>
          ) : (
            <div className={`sectional-editor-block ${section.type === BLOCK_PARAGRAPH ? 'paragraph-editor-block' : 'step-editor-block'}`}>
              <div className="sectional-editor-block-header">
                {section.type === BLOCK_STEP ? (
                  <>
                    <span className="step-number-badge">Step {getStepNumber(sections, index)}</span>
                    <input
                      type="text"
                      className="sectional-editor-block-title"
                      value={section.title}
                      onChange={(e) => updateSection(index, 'title', e.target.value)}
                      placeholder="Brief label (e.g. Open Settings)"
                    />
                  </>
                ) : (
                  <span className="paragraph-label">Paragraph</span>
                )}
                <div className="sectional-editor-block-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-small"
                    onClick={() => duplicateSection(index)}
                    title="Duplicate this step"
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-small"
                    onClick={() => moveSection(index, -1)}
                    disabled={index === 0}
                    title="Move step up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-small"
                    onClick={() => moveSection(index, 1)}
                    disabled={index === sections.length - 1}
                    title="Move step down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-danger admin-btn-small"
                    onClick={() => removeSection(index)}
                    disabled={sections.length <= 1}
                    title="Remove this step"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="sectional-editor-block-content">
                <RichDescriptionEditor
                  value={section.content}
                  onChange={(v) => updateSection(index, 'content', v)}
                  placeholder={section.type === BLOCK_PARAGRAPH ? 'Add introductory text, notes, or other content…' : (placeholder || 'Describe what to do in this step. Add images, links, or formatting as needed.')}
                  minHeight={180}
                  previewLabel={section.type === BLOCK_STEP && section.title ? `Step ${getStepNumber(sections, index)}: ${section.title}` : section.type === BLOCK_PARAGRAPH ? 'Paragraph' : (section.type === BLOCK_STEP ? `Step ${getStepNumber(sections, index)}` : null)}
                />
              </div>
            </div>
          )}
          <div className="sectional-editor-insert-between">
            <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => insertAfter(index, BLOCK_STEP)} title="Add step below">+ Step</button>
            <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => insertAfter(index, BLOCK_PARAGRAPH)} title="Add paragraph below">+ Paragraph</button>
            <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => insertAfter(index, BLOCK_STEP_GROUP_START)} title="Add step group start below">+ Reset</button>
          </div>
        </React.Fragment>
      ))}
      <div className="sectional-editor-footer">
        <button type="button" className="admin-btn admin-btn-primary admin-btn-small" onClick={addStep}>
          + Add step
        </button>
        <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={addParagraph}>
          + Add paragraph
        </button>
        <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={addStepGroupStart} title="Start a new step sequence (Step 1, 2, 3...)">
          Reset step counter
        </button>
      </div>
    </div>
  );
}
